import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGrades, useGradeSubjects, useSchoolAddGrade, useUserInfo, useNationalExams } from '../../react-query/queries'
import { useAuth } from '../../utils/Auth/AuthProvider'
import { Button as FlowButton } from 'flowbite-react'
import ExamProgressStepper from './ExamProgressStepper'
import ExamQuestionAddCard from './ExamQuestionAddCard'
import ExamQuestionsMeta from './ExamQuestionsMeta'
import { useSchoolExamQuestionAdd } from '../../react-query/queries'

const ExamAddWizard: React.FC = () => {
    const { token } = useAuth()
    

    const [step, setStep] = useState<number>(1)
    const [gradeStateId, setGradeStateId] = useState<string>(() => { try { return localStorage.getItem('add_exam_grade') || '-1' } catch { return '-1' } })
    const [subjectStateId, setSubjectStateId] = useState<string>(() => { try { return localStorage.getItem('add_exam_subject') || '-1' } catch { return '-1' } })
    const [isAdding, setISAdding] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    const [createdExamId, setCreatedExamId] = useState<string | null>(null)
    const [pendingQuestions, setPendingQuestions] = useState<any[] | null>(null)
    const [questionsExtracted, setQuestionsExtracted] = useState<boolean>(false) // New state to track if questions are extracted
    const [isExtractingQuestions, setIsExtractingQuestions] = useState<boolean>(false) // New state for loading during extraction
    // allow child to register a save handler for floating controls
    const [childSaveFn, setChildSaveFn] = useState<(() => void) | null>(null)
    const [extractQuestionsFn, setExtractQuestionsFn] = useState<(() => void) | null>(null) // New state to hold the extract function from child
    const { mutateAsync: schoolExamQuestionAdd } = useSchoolExamQuestionAdd()
    const navigate = useNavigate()

    const { status: gradeStatus, data: gradeData } = useGrades(token)
    const { status: subjectStatus, data: subjectData } = useGradeSubjects(token, gradeStateId)
    const { mutate: schoolAddExam } = useSchoolAddGrade()

    const memoizedOnProceedToMeta = useCallback((qs: any[]) => { setPendingQuestions(qs); setQuestionsExtracted(true); }, [setPendingQuestions, setQuestionsExtracted]);
    const memoizedOnRegisterExtractFn = useCallback((fn: () => void) => setExtractQuestionsFn(() => fn), [setExtractQuestionsFn]);
    const memoizedOnQuestionsExtracted = useCallback(() => setQuestionsExtracted(true), [setQuestionsExtracted]);

    const [formdata, setFormdata] = useState({ name: '', given_time: 60, description: '' })
    const [isNational, setIsNational] = useState<boolean>(false)
    const [selectedNationalExam, setSelectedNationalExam] = useState<string>('-1')

    const { role } = useAuth()

    // fetch user info (which may include institution_type)
    const { data: userData } = useUserInfo()

    // prefer institution_type from user info; fallback to role-derived mapping
    const targetLevel: 'school' | 'college_university' | undefined = ((): any => {
        const raw = (userData?.institution_type ?? userData?.institution_name ?? '') as string
        const instType = String(raw || '').trim().toLowerCase()
        if (instType.includes('school')) return 'school'
        if (instType.includes('college') || instType.includes('university') || instType.includes('college_university')) return 'college_university'
        // fallback to role
        const r = String(role ?? '').toLowerCase()
        if (r === 'school') return 'school'
        if (r.includes('college') || r.includes('university') || r === 'college_university') return 'college_university'
        return undefined
    })()

    // always enable the national exams fetch; targetLevel may be undefined which returns all
    const { status: nationalStatus, data: nationalData } = useNationalExams(targetLevel, true)

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target
        setFormdata((prev) => ({ ...prev, [name]: name === 'given_time' ? Number(value) : value }))
    }

    const isValidStep1 = () => gradeStateId !== '-1'
    const isValidStep2 = () => subjectStateId !== '-1'
    const isValidStep3 = () => formdata.name.trim().length >= 3 && Number(formdata.given_time) > 0 && Number(formdata.given_time) <= 300

    const createExam = async (e?: React.FormEvent) => {
        if (e) e.preventDefault()
        if (isAdding) return
        setSubmitError(null)
        if (!isValidStep3()) { setSubmitError('Please complete all fields correctly before continuing.'); return }
        // when national exam is toggled on, require selection of a national exam
        if (isNational) {
            if (!selectedNationalExam || selectedNationalExam === '-1') {
                setSubmitError('Please choose a national exam or turn off the National Exam option before continuing.')
                return
            }
        }
        setISAdding(true)
        const postData = new FormData()
        postData.append('name', formdata.name.trim())
        postData.append('time', `${formdata.given_time}`)
        postData.append('desc', formdata.description?.trim() || '')
        postData.append('grade_id', gradeStateId)
        postData.append('subject_id', subjectStateId)
        // optional: exam scope and national exam id
        if (isNational) {
            postData.append('exam_scope', 'national')
            if (selectedNationalExam && selectedNationalExam !== '-1') postData.append('national_exam', selectedNationalExam)
        } else {
            postData.append('exam_scope', 'none')
        }

        schoolAddExam({ toBeValidated: token, data: postData } as any, {
            onSuccess: (data: any) => {
                setISAdding(false)
                if (data && data['done'] === 'done') {
                    try { localStorage.setItem('add_exam_grade', gradeStateId); localStorage.setItem('add_exam_subject', subjectStateId) } catch {}
                    // instead of navigating, keep the wizard on the same URL and move to step 4
                    const newId = String(data['exam_id'])
                    setCreatedExamId(newId)
                    setStep(4)
                } else {
                    setSubmitError('Failed to create exam. Please try again.')
                }
            },
            onError: (err: any) => {
                setISAdding(false)
                const msg = err?.response?.data?.detail || err?.message || 'Request failed'
                setSubmitError(typeof msg === 'string' ? msg : 'Failed to create exam.')
            }
        } as any)
    }

    const renderGradeTile = (grade: { id: number; grade_name: string }) => {
        const selected = String(grade.id) === gradeStateId
        return (
            <button key={grade.id} type="button" onClick={() => { setGradeStateId(String(grade.id)); setSubjectStateId('-1'); }} className={`rounded-lg border p-4 text-left transition ${selected ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/40' : 'border-stroke hover:border-primary/60 hover:bg-primary/5'}`}>
                <div className="text-sm text-bodydark2">Grade</div>
                <div className="text-lg font-semibold">{grade.grade_name}</div>
            </button>
        )
    }

    const renderSubjectTile = (subject: { id: number; subject_name: string }) => {
        const selected = String(subject.id) === subjectStateId
        return (
            <button key={subject.id} type="button" onClick={() => setSubjectStateId(String(subject.id))} className={`rounded-lg border p-4 text-left transition ${selected ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/40' : 'border-stroke hover:border-primary/60 hover:bg-primary/5'}`}>
                <div className="text-lg font-semibold">{subject.subject_name}</div>
            </button>
        )
    }

    const handleSaveQuestions = useCallback(async (qs: any[], options?: any) => {
        try {
            const resp = await schoolExamQuestionAdd({ toBeValidated: token, data: qs as any, exam_id: String(createdExamId || '') })
            try {
                if (!options?.partial && createdExamId) navigate(`/exams/exam-detail/${createdExamId}`)
            } catch (navErr) {
                console.warn('Navigation after save failed', navErr)
            }
            return resp
        } catch (err) {
            console.error('Failed to save questions', err)
            throw err
        }
    }, [schoolExamQuestionAdd, token, createdExamId, navigate])

    const handleRegisterSave = useCallback((fn: () => void) => {
        setChildSaveFn(() => fn)
    }, [setChildSaveFn])

    return (
        <div className="col-span-12 rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default sm:px-7.5">
            <div className="max-w-full overflow-x-auto">
                <h4 className="mb-4 text-xl font-semibold text-black">Create a new exam</h4>
            </div>
            <ExamProgressStepper current={step} />

            {/* National exam controls intentionally removed from top - they appear only in Step 3 below the heading */}

            <div className="mt-4">
                {step === 1 && (
                    <div>
                        <h2 className="pb-3 pt-1 text-base font-semibold">1. Choose Grade</h2>
                        {gradeStatus === 'success' ? (
                            <div className="grid gap-4 xsm:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                                {gradeData.grades.map((g: any) => renderGradeTile(g))}
                            </div>
                        ) : (
                            <div className="text-bodydark2">Loading grades…</div>
                        )}
                    </div>
                )}

                {step === 2 && (
                    <div>
                        <h2 className="pb-3 pt-1 text-base font-semibold">2. Choose Subject</h2>
                        {subjectStatus === 'success' ? (
                            <div className="grid gap-4 xsm:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                                {subjectData.subjects.map((s: any) => renderSubjectTile(s))}
                            </div>
                        ) : (
                            <div className="text-bodydark2">Loading subjects…</div>
                        )}
                    </div>
                )}

                {step === 3 && (
                    <div>
                        <h2 className="pb-3 pt-1 text-base font-semibold">3. Exam Details</h2>
                        {submitError && <div className="mb-3 rounded border border-red-300 bg-red-50 p-2.5 text-red-700">{submitError}</div>}
                        <form onSubmit={createExam}>
                            <div className="p-6.5">
                                    {/* Move the national exam controls above the Name/Time inputs as requested */}
                                    <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                                        <div>
                                            <label className="mb-2.5 block text-black">National Exam</label>
                                            <div className="flex items-center gap-3">
                                                <label className={`relative m-0 block h-7.5 w-14 rounded-full ${isNational ? 'bg-primary' : 'bg-stroke'}`}>
                                                    <input
                                                        type="checkbox"
                                                        aria-label="Mark as national exam"
                                                        checked={isNational}
                                                        onChange={() => { setIsNational(!isNational); if (!isNational) setSelectedNationalExam('-1') }}
                                                        className="dur absolute top-0 z-50 m-0 h-full w-full cursor-pointer opacity-0"
                                                    />
                                                    <span className={`absolute top-1/2 left-[3px] flex h-6 w-6 -translate-y-1/2 translate-x-0 items-center justify-center rounded-full bg-white shadow-switcher duration-75 ease-linear ${isNational && '!right-[3px] !translate-x-full'}`}>
                                                        <span className="dark:hidden">
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.99992 12.6666C10.5772 12.6666 12.6666 10.5772 12.6666 7.99992C12.6666 5.42259 10.5772 3.33325 7.99992 3.33325C5.42259 3.33325 3.33325 5.42259 3.33325 7.99992C3.33325 10.5772 5.42259 12.6666 7.99992 12.6666Z" fill="#969AA1"/></svg>
                                                        </span>
                                                        <span className="hidden dark:inline-block">
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.3533 10.62C14.2466 10.44 13.9466 10.16 13.1999 10.2933C12.7866 10.3667 12.3666 10.4 11.9466 10.38C10.3933 10.3133 8.98659 9.6 8.00659 8.5C7.13993 7.53333 6.60659 6.27333 6.59993 4.91333C6.59993 4.15333 6.74659 3.42 7.04659 2.72666C7.33993 2.05333 7.13326 1.7 6.98659 1.55333C6.83326 1.4 6.47326 1.18666 5.76659 1.48C3.03993 2.62666 1.35326 5.36 1.55326 8.28666C1.75326 11.04 3.68659 13.3933 6.24659 14.28C6.85993 14.4933 7.50659 14.62 8.17326 14.6467C8.27993 14.6533 8.38659 14.66 8.49326 14.66C10.7266 14.66 12.8199 13.6067 14.1399 11.8133C14.5866 11.1933 14.4666 10.8 14.3533 10.62Z" fill="#969AA1"/></svg>
                                                        </span>
                                                    </span>
                                                </label>
                                                <div className="text-sm">Mark as national exam</div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="mb-2.5 block text-black">Choose National Exam</label>
                                            <select aria-label="National exam select" disabled={!isNational || nationalStatus !== 'success'} value={selectedNationalExam} onChange={(e) => setSelectedNationalExam(e.target.value)} className={`w-full rounded border-[1.5px] border-stroke py-3 px-5 ${!isNational ? 'bg-gray-50 text-bodydark2' : 'bg-white'}`}>
                                                <option value="-1">-- Select national exam --</option>
                                                {nationalStatus === 'success' && (nationalData || []).map((n: any) => (
                                                    <option key={n.id} value={String(n.id)}>{n.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                {/* Re-insert Exam Name / Time and Description fields (kept original layout) */}
                                <div className="mb-4.5 flex flex-col gap-6 xl:flex-row">
                                    <div className="w-full xl:w-1/2">
                                        <label className="mb-2.5 block text-black">Exam Name</label>
                                        <input type="text" placeholder="e.g. Midterm Assessment" name="name" value={formdata.name} onChange={handleInputChange} className="w-full rounded border-[1.5px] border-stroke py-3 px-5" />
                                    </div>
                                    <div className="w-full xl:w-1/2">
                                        <label className="mb-2.5 block text-black">Time (minutes)</label>
                                        <input type="number" min={1} max={300} placeholder="60" name="given_time" value={formdata.given_time} onChange={handleInputChange} className="w-full rounded border-[1.5px] border-stroke py-3 px-5" />
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <label className="mb-2.5 block text-black">Description</label>
                                    <textarea rows={5} placeholder="Add an optional description" name="description" value={formdata.description} onChange={handleInputChange} className="w-full rounded border-[1.5px] border-stroke py-3 px-5" />
                                </div>

                                {/* Inline controls removed for Step 3 (use floating controls) */}
                            </div>
                        </form>
                    </div>
                )}

                {step === 4 && createdExamId && (
                    <div>
                        <h2 className="pb-3 pt-1 text-base font-semibold">4. Add Questions</h2>
                        {/* render the same Add Questions card but pass examId from state */}
                        {/* Import dynamically to avoid circular issues in some setups */}
                        <div className="mt-4">
                            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                            {/* @ts-ignore */}
                            <ExamQuestionAddCard
                                examId={createdExamId}
                                onProceedToMeta={memoizedOnProceedToMeta}
                                onRegisterExtractFn={memoizedOnRegisterExtractFn}
                                onQuestionsExtracted={memoizedOnQuestionsExtracted}
                                isExtracting={isExtractingQuestions}
                                setIsExtracting={setIsExtractingQuestions}
                            />
                        </div>
                    </div>
                )}

                        {step === 5 && createdExamId && pendingQuestions && (
                    <div>
                        <ExamQuestionsMeta
                            subjectId={subjectStateId}
                            subjectName={subjectData?.subjects?.find((s: any) => String(s.id) === subjectStateId)?.subject_name}
                            gradeId={gradeStateId}
                            examId={createdExamId}
                            questions={pendingQuestions}
                            onBack={() => setStep(4)}
                            onSave={handleSaveQuestions}
                            onRegisterSave={handleRegisterSave}
                        />
                    </div>
                )}
            </div>

            {/* Inline footer removed — navigation is provided by the floating bottom-center controls */}

            {/* Floating center controls - bottom center */}
            <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-50 flex items-center gap-3">
                {/* Grade step: only Next */}
                {step === 1 && (
                    <FlowButton size="lg" onClick={() => { if (!isValidStep1()) { setSubmitError('Please select a grade to continue.'); return } setSubmitError(null); setStep(2) }}>
                        Next
                    </FlowButton>
                )}

                {/* Subject step: Back + Next */}
                {step === 2 && (
                    <>
                        <button className="px-4 py-2 border rounded bg-white text-gray-700 border-gray-300 hover:bg-gray-100" onClick={() => setStep(1)}>Back</button>
                        <FlowButton size="lg" onClick={() => { if (!isValidStep2()) { setSubmitError('Please select a subject to continue.'); return } setSubmitError(null); setStep(3) }}>Next</FlowButton>
                    </>
                )}

                {/* Details step: Back + Create exam & Next (trigger createExam) */}
                {step === 3 && (
                    <>
                        <button className="px-4 py-2 border rounded bg-white text-gray-700 border-gray-300 hover:bg-gray-100" onClick={() => setStep(2)}>Back</button>
                        <FlowButton size="lg" onClick={() => { if (!isAdding) createExam(); }} disabled={!isValidStep3() || isAdding}>{isAdding ? 'Creating…' : 'Create Exam & Next'}</FlowButton>
                    </>
                )}

                {/* Questions step: Back + Extract/Next */}
                {step === 4 && (
                    <>
                        <button className="px-4 py-2 border rounded bg-white text-gray-700 border-gray-300 hover:bg-gray-100" onClick={() => setStep(3)}>Back</button>
                        {!questionsExtracted ? (
                            <FlowButton size="lg" onClick={() => { if (extractQuestionsFn) { extractQuestionsFn(); } }} disabled={isExtractingQuestions}>
                                {isExtractingQuestions ? 'Extracting...' : 'Extract Questions'}
                            </FlowButton>
                        ) : (
                            <FlowButton size="lg" onClick={() => { if (pendingQuestions) { setStep(5); } }}>
                                Next: Chapter and Explanation
                            </FlowButton>
                        )}
                    </>
                )}

                {/* Chapter & Explanation step: Back + Save */}
                {step === 5 && (
                    <>
                        <button className="px-4 py-2 border rounded bg-white text-gray-700 border-gray-300 hover:bg-gray-100" onClick={() => setStep(4)}>Back</button>
                        <FlowButton size="lg" onClick={() => { if (childSaveFn) childSaveFn(); else alert('Save handler not registered yet. Use the Save button in the page.') }}>Save</FlowButton>
                    </>
                )}
            </div>
        </div>
    )
}

export default ExamAddWizard
